import { ErrorType, PDFData } from '@readcloud/data';
import React from 'react';
import { prepareWebDocument } from '../utils/prepareWebDocument';
import { prepareTauriDocument } from '../utils/prepareTauriDocument';
import { cancelTauriDownloadByUrl } from '@readcloud/fetch-wrapper';

const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

export const usePrepareDocument = (props: {
  isbn?: string;
  minor?: number;
  longId?: string;
  onBeforePrepare?: () => Promise<any>;
}) => {
  const [data, setData] = React.useState<PDFData>();
  const [type, setType] = React.useState<string>();
  const [error, setError] = React.useState<ErrorType>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [progress, setProgress] = React.useState<number>(0);
  const cancelRef = React.useRef<boolean>(false);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const currentDownloadRef = React.useRef<{ id: string; url: string } | null>(
    null
  );

  const prepareDocument = React.useCallback(
    async (fileUrl: string) => {
      if (!fileUrl) {
        setError({ type: 'DocumentNotFound' });
        setLoading(false);
        return;
      }

      setError(undefined);
      setProgress(0);
      setLoading(true);
      setType(undefined);
      cancelRef.current = false;
      // Reset abort controller per request (web may use it)
      abortControllerRef.current?.abort();
      abortControllerRef.current =
        typeof AbortController !== 'undefined' ? new AbortController() : null;

      try {
        // Select the appropriate document preparation function based on the environment
        const prepareDocumentFn = isTauri
          ? prepareTauriDocument
          : prepareWebDocument;

        const result = await prepareDocumentFn({
          fileUrl,
          isbn: props.isbn,
          minor: props.minor,
          longId: props.longId,
          onBeforePrepare: props.onBeforePrepare,
          onProgress: setProgress,
          // Optional: pass signal for web impls that support it
          signal: abortControllerRef.current?.signal,
          // Hook to capture tauri download id when intercepted
          onDownloadStart: ({ id, url }) => {
            currentDownloadRef.current = { id, url };
          },
        });
        if (!cancelRef.current) {
          setData(result.data);
          setType(result.type);
          setError(result.error);
          setLoading(result.loading);
        }
      } catch (e) {
        if (!cancelRef.current) {
          setError({
            type: 'DownloadError',
            error: e as Error,
          });
          setLoading(false);
        }
      }
    },
    [props.isbn, props.minor, props.longId, props.onBeforePrepare]
  );

  const cancelPrepare = React.useCallback(() => {
    cancelRef.current = true;
    try {
      abortControllerRef.current?.abort();
    } catch {}
    // If in Tauri, also attempt to cancel the current media/file download
    try {
      if (isTauri && currentDownloadRef.current?.url) {
        cancelTauriDownloadByUrl(currentDownloadRef.current.url);
      }
    } catch {}
    setLoading(false);
  }, []);

  return {
    data,
    type,
    error,
    loading,
    progress,
    prepareDocument,
    cancelPrepare,
  };
};
